'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import type { OnwardTicketAnalysisResponse } from '@/app/api/anthropic/onward-ticket-analysis/route';
import type { EmbassyStatusCheckResponse } from '@/app/api/anthropic/embassy-status-check/route';
import type { ApiResponse } from '@/lib/types/anthropic';
import { PNRGenerator, type GeneratedPNR } from '@/lib/pnr-generator';
import { DocumentGenerator, type OnwardTicketDocument } from '@/lib/document-generator';

interface BookingFormProps {
  selectedPlan: string;
}

type FormStep = 'details' | 'analysis' | 'embassy-check' | 'confirmation';

export default function BookingForm({ selectedPlan }: BookingFormProps) {
  const [step, setStep] = useState<FormStep>('details');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    passportNumber: '',
    nationality: '',
    from: '',
    to: '',
    departDate: '',
    returnDate: '',
    purpose: 'visa'
  });

  const [analysis, setAnalysis] = useState<OnwardTicketAnalysisResponse | null>(null);
  const [embassyStatus, setEmbassyStatus] = useState<EmbassyStatusCheckResponse | null>(null);
  const [pnrDetails, setPnrDetails] = useState<GeneratedPNR | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAnalyze = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/anthropic/onward-ticket-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          travelDetails: {
            from: formData.from,
            to: formData.to,
            departDate: formData.departDate,
            returnDate: formData.returnDate || undefined,
            purpose: formData.purpose as 'visa' | 'embassy' | 'airline',
            nationality: formData.nationality,
            passportNumber: formData.passportNumber,
          },
          passengerInfo: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            dateOfBirth: formData.dateOfBirth,
          },
        }),
      });

      const data: ApiResponse<OnwardTicketAnalysisResponse> = await res.json();

      if (data.success) {
        setAnalysis(data.data);
        setStep('embassy-check');
        // Automatically fetch embassy status
        await fetchEmbassyStatus();
      } else {
        setError(data.error.message);
        if (!data.error.isInternal) {
          console.error('Error analyzing travel details:', data.error.message);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to analyze travel details.';
      setError(errorMessage);
      console.error('Error analyzing travel details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmbassyStatus = async () => {
    try {
      const res = await fetch('/api/anthropic/embassy-status-check', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          booking: {
            destination: formData.to,
            departureDate: formData.departDate,
            returnDate: formData.returnDate || formData.departDate,
            nationality: formData.nationality,
            passportNumber: formData.passportNumber,
            travelers: 1,
            airline: 'TBD',
            flightNumber: 'TBD',
            pnr: 'PENDING',
          },
        }),
      });

      const data: ApiResponse<EmbassyStatusCheckResponse> = await res.json();

      if (data.success) {
        setEmbassyStatus(data.data);
      }
    } catch (err) {
      console.error('Error fetching embassy status:', err);
    }
  };

  const handleConfirmBooking = () => {
    setLoading(true);

    setTimeout(() => {
      const pnr = PNRGenerator.generate(selectedPlan as 'basic' | 'standard' | 'premium');
      setPnrDetails(pnr);
      setStep('confirmation');
      setLoading(false);
    }, 1500);
  };

  const handleDownloadPDF = () => {
    if (!pnrDetails || !analysis) return;

    const documentData: OnwardTicketDocument = {
      pnr: pnrDetails,
      passengerInfo: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        dateOfBirth: formData.dateOfBirth,
        passportNumber: formData.passportNumber,
        nationality: formData.nationality,
      },
      flightDetails: {
        from: formData.from,
        to: formData.to,
        departDate: formData.departDate,
        returnDate: formData.returnDate || undefined,
      },
      plan: selectedPlan as 'basic' | 'standard' | 'premium',
    };

    const pdf = DocumentGenerator.generateOnwardTicketPDF(documentData);
    DocumentGenerator.downloadPDF(pdf, `onward-ticket-${pnrDetails.bookingReference}.pdf`);
  };

  // Confirmation Step
  if (step === 'confirmation') {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
            <Icon name="CheckCircleIcon" size={48} className="text-green-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Booking Confirmed!</h3>
          <p className="text-gray-600 mb-6">
            Your onward ticket has been generated and sent to your email.
          </p>

          {/* PNR Details */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
              <div>
                <p className="text-xs text-gray-500 mb-1">PNR CODE</p>
                <p className="text-2xl font-black text-blue-600">{pnrDetails?.pnr}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">BOOKING REFERENCE</p>
                <p className="text-lg font-bold text-gray-900">{pnrDetails?.bookingReference}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">AIRLINE</p>
                <p className="text-sm font-semibold text-gray-900">{pnrDetails?.airline} ({pnrDetails?.airlineCode})</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">VALID UNTIL</p>
                <p className="text-sm font-semibold text-gray-900">
                  {pnrDetails && new Date(pnrDetails.validUntil).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
            <button
              onClick={handleDownloadPDF}
              className="flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors"
            >
              <Icon name="ArrowDownTrayIcon" size={20} />
              Download PDF Ticket
            </button>
            <button
              onClick={() => {
                setStep('details');
                setPnrDetails(null);
                setAnalysis(null);
              }}
              className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-bold hover:bg-gray-200 transition-colors"
            >
              Book Another Ticket
            </button>
          </div>

          {/* Important Notes */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
            <div className="flex items-start gap-2">
              <Icon name="InformationCircleIcon" size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-700">
                <p className="font-bold mb-2">Important Information:</p>
                <ul className="space-y-1 text-xs">
                  <li>• Your PNR is active in the {pnrDetails?.airline} booking system</li>
                  <li>• Embassy officials can verify this booking directly with the airline</li>
                  <li>• Keep your booking reference safe for any modifications</li>
                  <li>• A confirmation email with PDF attachment has been sent to {formData.email}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Analysis & Embassy Status Step
  if (step === 'embassy-check' && analysis && embassyStatus) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <div className="animate-fadeIn">
          <button
            onClick={() => setStep('details')}
            className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <Icon name="ArrowLeftIcon" size={20} />
            Back to Details
          </button>

          <h2 className="text-2xl font-black text-gray-900 mb-6">AI Travel Analysis & Embassy Status</h2>

          {/* Visa Requirements */}
          <div className="mb-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="DocumentTextIcon" size={20} className="text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Visa Requirements</h3>
                <div className="space-y-2 text-sm">
                  <p><span className="font-semibold">Required:</span> {analysis.visaRequirements.required ? 'Yes' : 'No'}</p>
                  <p><span className="font-semibold">Type:</span> {analysis.visaRequirements.type}</p>
                  <p><span className="font-semibold">Processing Time:</span> {analysis.visaRequirements.processingTime}</p>
                  <p className="text-gray-600 italic">{analysis.visaRequirements.notes}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Embassy Status Section */}
          {embassyStatus && (
            <div className="mb-6 space-y-4">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Icon name="BuildingOffice2Icon" size={24} className="text-blue-600" />
                Live Embassy Status
              </h3>

              {/* Embassy Info */}
              <div className="bg-white rounded-xl p-6 border-2 border-blue-200">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="MapPinIcon" size={20} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                      {embassyStatus.destination.country}
                    </h4>
                    <div className="space-y-1 text-sm">
                      <p><span className="font-semibold">Embassy:</span> {embassyStatus.destination.embassyLocation}</p>
                      <p><span className="font-semibold">Contact:</span> {embassyStatus.destination.contactInfo}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Live Status */}
              <div className={`rounded-xl p-6 border-2 ${
                embassyStatus.liveStatus.embassyOperational
                  ? 'bg-green-50 border-green-200' :'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    embassyStatus.liveStatus.embassyOperational ? 'bg-green-600' : 'bg-red-600'
                  }`}>
                    <Icon name="SignalIcon" size={20} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-gray-900 mb-2">Current Status</h4>
                    <div className="space-y-2 text-sm">
                      <p>
                        <span className="font-semibold">Status:</span>{' '}
                        <span className={embassyStatus.liveStatus.embassyOperational ? 'text-green-700' : 'text-red-700'}>
                          {embassyStatus.liveStatus.embassyOperational ? 'Operational' : 'Closed/Limited'}
                        </span>
                      </p>
                      <p><span className="font-semibold">Processing Time:</span> {embassyStatus.liveStatus.currentProcessingTime}</p>
                      <p><span className="font-semibold">Appointments:</span> {embassyStatus.liveStatus.appointmentAvailability}</p>
                    </div>
                    {embassyStatus.liveStatus.alerts.length > 0 && (
                      <div className="mt-3 space-y-1">
                        {embassyStatus.liveStatus.alerts.map((alert, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <Icon name="BellAlertIcon" size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-gray-700">{alert}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Document Validity */}
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Icon name="IdentificationIcon" size={20} className="text-purple-600" />
                  Document Validity Status
                </h4>
                <div className="space-y-2">
                  {embassyStatus.documentValidity.map((doc, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-white">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-sm text-gray-900">{doc.documentType}</p>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          doc.status === 'valid' ? 'bg-green-100 text-green-700' :
                          doc.status === 'expiring-soon'? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {doc.status.replace('-', ' ').toUpperCase()}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">{doc.notes}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Recommended Ticket */}
          <div className="mb-6 bg-green-50 rounded-xl p-6 border-2 border-green-200">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="SparklesIcon" size={20} className="text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Recommended Ticket Plan</h3>
                <div className="mb-3">
                  <span className="inline-block bg-green-600 text-white px-3 py-1 rounded-full text-sm font-bold uppercase">
                    {analysis.ticketRecommendation.ticketType}
                  </span>
                  <span className="ml-3 text-2xl font-black text-gray-900">£{analysis.ticketRecommendation.price}</span>
                </div>
                <p className="text-sm text-gray-700 mb-3">{analysis.ticketRecommendation.reasoning}</p>
                <ul className="space-y-1">
                  {analysis.ticketRecommendation.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                      <Icon name="CheckCircleIcon" size={16} className="text-green-600" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Document Checklist */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Icon name="ClipboardDocumentCheckIcon" size={20} className="text-blue-600" />
              Document Checklist
            </h3>
            <div className="space-y-4">
              {analysis.documentChecklist.map((category, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-3">{category.category}</h4>
                  <ul className="space-y-2">
                    {category.items.map((item, itemIdx) => (
                      <li key={itemIdx} className="flex items-start gap-2">
                        <Icon 
                          name={item.required ? "ExclamationCircleIcon" : "InformationCircleIcon"} 
                          size={16} 
                          className={item.required ? "text-red-600 flex-shrink-0 mt-0.5" : "text-blue-600 flex-shrink-0 mt-0.5"} 
                        />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900">
                            {item.name} {item.required && <span className="text-red-600">*</span>}
                          </p>
                          <p className="text-xs text-gray-600">{item.description}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Confirm Booking Button */}
          <div className="flex justify-center">
            <button
              onClick={handleConfirmBooking}
              disabled={loading}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Processing...
                </span>
              ) : (
                'Confirm Booking - £${analysis.ticketRecommendation.price}'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Details Form Step
  return (
    <div className="bg-white rounded-2xl shadow-xl p-8">
      <h2 className="text-2xl font-black text-gray-900 mb-6">Passenger & Flight Details</h2>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
          <Icon name="ExclamationCircleIcon" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <form onSubmit={handleAnalyze} className="space-y-6">
        {/* Passenger Information */}
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Icon name="UserIcon" size={20} className="text-blue-600" />
            Passenger Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="John"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Doe"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date of Birth *
              </label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Passport Number *
              </label>
              <input
                type="text"
                name="passportNumber"
                value={formData.passportNumber}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="AB1234567"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nationality *
              </label>
              <input
                type="text"
                name="nationality"
                value={formData.nationality}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="British"
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Icon name="EnvelopeIcon" size={20} className="text-blue-600" />
            Contact Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="john.doe@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="+44 20 1234 5678"
              />
            </div>
          </div>
        </div>

        {/* Flight Details */}
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Icon name="PaperAirplaneIcon" size={20} className="text-blue-600" />
            Flight Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From (Departure City) *
              </label>
              <input
                type="text"
                name="from"
                value={formData.from}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="London (LHR)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To (Destination) *
              </label>
              <input
                type="text"
                name="to"
                value={formData.to}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="New York (JFK)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Departure Date *
              </label>
              <input
                type="date"
                name="departDate"
                value={formData.departDate}
                onChange={handleChange}
                required
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Return Date (Optional)
              </label>
              <input
                type="date"
                name="returnDate"
                value={formData.returnDate}
                onChange={handleChange}
                min={formData.departDate || new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Purpose *
              </label>
              <select
                name="purpose"
                value={formData.purpose}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="visa">Visa Application</option>
                <option value="embassy">Embassy Interview</option>
                <option value="airline">Airline Check-in Requirement</option>
              </select>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-center pt-4">
          <button
            type="submit"
            disabled={loading}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Analyzing with AI...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Icon name="SparklesIcon" size={20} />
                Analyze Travel Requirements
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}